import { useRef } from "react";
import { PrismInputTime } from "@prism-ui/react";
import { DateTime } from "luxon";

/**
 * A time-of-day picker bound to a wall-clock "HH:mm" string.
 *
 * `prism-input-time` needs a lot of handling to behave as a controlled input, all of which is
 * contained here (each behaviour below was measured in design-lab/input-time-probe.html):
 *
 *  - It rejects bare times ("09:30", "9:30 AM") with an "Invalid date format" warning and
 *    only accepts a full datetime, so the value is padded out to one.
 *  - It reads that datetime in the *browser's* timezone. Rather than fight it, the seed is
 *    built from the digits we want displayed, so what the component shows is the fair's
 *    wall clock regardless of where the officer happens to be sitting.
 *  - It emits `prismChange` on mount — twice — reporting the current clock rather than the
 *    seeded value. Committing those would silently overwrite a saved time with "now" every
 *    time the dialog opened, so a change is only accepted once the user has actually
 *    touched the control.
 *  - It only reads `value` at mount; later assignments do not move the control. `remountKey`
 *    lets a caller force a fresh instance when the underlying value changes from elsewhere.
 */
const TimeOfDayPicker = ({
  legend,
  value,
  onChange,
  remountKey,
}: {
  legend: string;
  /** Wall-clock time as "HH:mm", or "" for unset. */
  value: string;
  onChange: (next: string) => void;
  remountKey?: string | number;
}) => {
  const touched = useRef(false);

  // Any date works — only the time half is ever read back out. A fixed one keeps the seed
  // stable so the component does not remount on every render.
  const seed = value ? `2000-01-01T${value}:00` : undefined;

  const handleChange = (detail?: { time?: string; iso?: string }) => {
    if (!touched.current) return;
    const raw = (detail?.time || "").trim();
    if (!raw) return;
    const parsed = DateTime.fromFormat(raw, "h:mm a");
    const fallback = DateTime.fromFormat(raw, "HH:mm");
    const chosen = parsed.isValid ? parsed : fallback;
    if (!chosen.isValid) return;
    onChange(chosen.toFormat("HH:mm"));
  };

  return (
    <div
      className="project-fairs-time-picker"
      onPointerDown={() => {
        touched.current = true;
      }}
      onKeyDown={() => {
        touched.current = true;
      }}
    >
      <PrismInputTime
        key={remountKey}
        legend={legend}
        as="12"
        value={seed}
        onPrismChange={(e: CustomEvent<{ time?: string; iso?: string }>) => handleChange(e.detail)}
      />
    </div>
  );
};

export default TimeOfDayPicker;
